<template>
  <div v-if="isLoading" class="loading-screen">
    <div class="loading-panel" role="status" aria-live="polite">
      <div class="logo-container">
        <img
          :src="logoSrc"
          alt="Grupo KC Logo"
          class="logo-image"
        />
      </div>
      <div class="loading-content">
        <div class="spinner-container" aria-hidden="true">
          <div class="spinner spinner-primary"></div>
          <div class="spinner spinner-secondary"></div>
        </div>
        <div class="loading-text">
          <p class="loading-message">{{ loadingMessage || 'Cargando...' }}</p>
          <p class="loading-submessage">{{ subMessage || 'Por favor espere un momento...' }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLoading } from '@/composables/useLoading';

interface Props {
  logoSrc?: string;
}

withDefaults(defineProps<Props>(), {
  logoSrc: 'https://cdngrupokc.azureedge.net/web/img/logos/GrupoKCLogo300.png',
});

const { isLoading, loadingMessage, subMessage } = useLoading();
</script>

<style scoped>
.loading-screen {
  position: fixed;
  inset: 0;
  background: rgba(12, 21, 37, 0.16);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  z-index: 9999;
}

.loading-panel {
  width: min(92vw, 360px);
  border-radius: 14px;
  border: 1px solid rgba(201, 216, 236, 0.85);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 40px rgba(17, 30, 52, 0.22);
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.logo-container {
  width: 64px;
  height: 64px;
  margin-bottom: 2px;
  position: relative;
  border-radius: 12px;
  border: 1px solid rgba(209, 221, 238, 0.9);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  box-sizing: border-box;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.spinner-container {
  position: relative;
  width: 52px;
  height: 52px;
}

.spinner {
  position: absolute;
  top: 0;
  left: 0;
  width: 52px;
  height: 52px;
  border: 3px solid;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-primary {
  border-color: #2563eb;
  border-top-color: transparent;
}

.spinner-secondary {
  border-color: #93c5fd;
  border-top-color: transparent;
  animation: spin 1s linear infinite, pulse 2s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.loading-text {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.loading-message {
  font-size: 16px;
  font-weight: 700;
  color: #22314a;
  margin: 0;
  line-height: 1.35;
}

.loading-submessage {
  font-size: 13px;
  color: #5f718d;
  margin: 0;
  line-height: 1.35;
}

@media (max-width: 640px) {
  .loading-panel {
    width: min(94vw, 320px);
    padding: 16px 14px;
  }

  .logo-container {
    width: 56px;
    height: 56px;
  }

  .loading-message {
    font-size: 15px;
  }

  .loading-submessage {
    font-size: 12px;
  }
}
</style>

